import { AttachFile, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormLabel,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type {
  FolderOption,
  SelectedFolder,
} from '@/components/molecules/FolderAutocomplete';
import { FolderAutocomplete } from '@/components/molecules/FolderAutocomplete';
import { useUploadDocumentMutation } from '@/hooks/useUploadDocumentMutation';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

export interface DocumentUploadFormProps {
  directories: Array<string>;
  onClose: () => void;
}

export const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  directories,
  onClose,
}) => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const uploadDocumentMutation = useUploadDocumentMutation();

  const folderOptions: Array<FolderOption> = [
    {
      name: '/',
      display: 'General',
    },
  ].concat(
    directories
      .filter((directory) => directory !== '/')
      .map((directory) => ({
        name: directory,
        display: directory,
      }))
  );

  const [folder, setFolder] = useState<SelectedFolder>(folderOptions[0]);
  const [file, setFile] = useState<File | undefined>();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFile(event.target.files?.[0]);
  };

  const handleUpload = async () => {
    if (file && folder) {
      setIsUploading(true);
      try {
        await uploadDocumentMutation.mutateAsync({
          dirName: typeof folder === 'string' ? folder : folder.name,
          doc: file,
        });
        snackbar.enqueueSnackbar('Uploaded file succesfully', {
          variant: 'success',
        });
        onClose();
      } catch {
        snackbar.enqueueSnackbar('Failed to upload file', {
          variant: 'error',
        });
      }
      setIsUploading(false);
    } else {
      snackbar.enqueueSnackbar('Failed to upload file', {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      sx={{
        marginLeft: 'auto',
        marginRight: 'auto',
        alignSelf: 'center',
      }}>
      <Grid
        container
        sx={{
          justifyContent: 'right',
          width: '100%',
        }}>
        <Button onClick={onClose}>
          <Close />
        </Button>
      </Grid>
      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
        }}>
        Upload a File
      </Typography>
      <Box
        sx={{
          py: 4,
        }}>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={2}
          sx={{
            justifyItems: 'center',
            alignItems: 'center',
            px: 4,
            width: '100%',
          }}>
          <Grid
            sx={{
              textAlign: 'center',
            }}
            size={{
              xs: 4,
              sm: 2,
            }}>
            <FormLabel>To folder</FormLabel>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 6,
            }}>
            <FolderAutocomplete
              folderOptions={folderOptions}
              selected={folder}
              setSelected={setFolder}
            />
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 4,
            }}></Grid>
          <Grid
            sx={{
              textAlign: 'center',
            }}
            size={{
              xs: 4,
              sm: 2,
            }}>
            <FormLabel>File</FormLabel>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 6,
            }}>
            <TextField fullWidth value={file?.name ?? ''} />
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 4,
            }}>
            <Button variant="contained" component="label">
              <AttachFile />
              Add
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            size={12}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                pt: 4,
              }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ width: theme.spacing(16) }}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleUpload}
                disabled={isUploading}
                sx={{ width: theme.spacing(16) }}>
                {isUploading && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      pr: 1,
                    }}>
                    <CircularProgress size={theme.spacing(3)} color="primary" />
                  </Box>
                )}
                Upload
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};
